type UserFullName = {
  first_name?: string | null
  last_name?: string | null
}

export const isEmailValid = (
  email: string | undefined
): boolean | undefined => {
  const tester =
    // eslint-disable-next-line no-useless-escape
    /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/
  if (email) {
    return tester.test(email)
  }
}

/**
 * Like `getFullName`, but accepts individual name parts and an email fallback
 * so callers can surface the best display value for a user without repeating
 * the same coalescing pattern at every site.
 */
export const composeDisplayName = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string => {
  const name = `${firstName ?? ''} ${lastName ?? ''}`.trim()

  return name || email || ''
}

export const getNameAcronym = (name?: string): string | null => {
  const str = name || ''
  const matches = str.match(/\b(\w)/g)

  return matches && matches.join('')
}

export const getFullName = (user?: UserFullName) => {
  if (!user) {
    return ''
  }

  const { first_name, last_name } = user

  return [first_name, last_name]?.filter(Boolean)?.join(' ')
}

export const getUserInitials = ({ last_name, first_name }: UserFullName) =>
  getInitials(getFullName({ last_name, first_name }))

export const getInitials = (name: string) =>
  name
    .split(' ')
    .map((word) => word[0]?.toUpperCase())
    .join('')
    .slice(0, 2)

/** Characters the backend accepts in a permission `code`. */
export const PERMISSION_CODE_PATTERN = /^[a-z0-9_.:-]+$/

/**
 * Derive a permission code from a human label, constrained to
 * `PERMISSION_CODE_PATTERN` (`^[a-z0-9_.:-]+$`). "Manage Users" → "manage_users".
 * Decomposes accents (NFKD) and drops the combining marks, then collapses every
 * other non-alphanumeric run to an underscore. Used to pre-fill the code field
 * while still letting the user override it.
 */
export const slugifyPermissionCode = (label: string): string =>
  label
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')

export const stringToHslColor = (str: string, s = 60, l = 50) => {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash)
  }

  const h = hash % 360

  return `hsl(${h}, ${s}%, ${l}%)`
}
